import React, { FormEvent, useCallback, useEffect, useState } from "react";

import { useTranslate } from "../translation/frontend";
import ErrorMessage, { useErrorLabel } from "./ErrorMessage";

const types = {
  TEXT: "text",
  EMAIL: "email",
  PHONE: "phone",
  DATE: "date",
  DATETIME: "datetime-local",
  TIME: "time",
  TEXTAREA: undefined,
};

type QuestionType = keyof typeof types;

const autoCompletes: Record<QuestionType, string | undefined> = {
  TEXT: undefined,
  EMAIL: "email",
  PHONE: "tel",
  DATE: undefined,
  DATETIME: undefined,
  TIME: undefined,
  TEXTAREA: undefined,
};

const patterns: Record<QuestionType, string | undefined> = {
  TEXT: undefined,
  EMAIL: undefined,
  PHONE: /^\+?[\d\s]{6,}/.source,
  DATE: undefined,
  DATETIME: undefined,
  TIME: undefined,
  TEXTAREA: undefined,
};

export default function TextInput({
  type,
  id,
  name,
  placeholder,
  defaultValue,
  required,
  characterLimit,
  onInvalid,
  onValid,
}: {
  type: QuestionType;
  id: string;
  name: string;
  placeholder?: string;
  defaultValue?: string;
  required: boolean;
  characterLimit: number | null;
  onInvalid: () => void;
  onValid: () => void;
}) {
  const [invalidMessage, setInvalidMessage] = useState("");
  const errorLabel = useErrorLabel(type);
  const [value, setValue] = useState(defaultValue ?? "");
  const translate = useTranslate();
  const [characterLimitParam, setCharacterLimitParam] = useState(characterLimit ?? undefined);

  const onChange = useCallback(
    (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const input: HTMLInputElement | HTMLTextAreaElement = e.currentTarget;
      setValue(input.value);
      // Check these fields explicitly rather than validity.valid as that includes the custom validity which we might have already set. "typeMismatch" is the email address checker
      const { valueMissing, patternMismatch, typeMismatch } = input.validity;
      if (valueMissing || patternMismatch || typeMismatch) {
        onInvalid();
        setInvalidMessage(errorLabel);
        input.setCustomValidity(errorLabel);
      } else if (characterLimit && input.value.length > characterLimit) {
        onInvalid();
        const message = translate("maxCharactersError", { count: characterLimit });
        setInvalidMessage(message);
        input.setCustomValidity(message);
      } else {
        onValid();
        setInvalidMessage("");
        input.setCustomValidity("");
      }
      if (type === "DATETIME") {
        // This is needed to work around a Mobile Safari bug
        // https://stackoverflow.com/questions/43747521/mobile-safari-10-3-1-datetime-local-enter-a-valid-value-error
        input.value = input.value.substr(0, 16);
      }
    },
    [characterLimit, errorLabel, onInvalid, onValid, translate, type],
  );

  const Tag = type === "TEXTAREA" ? "textarea" : "input";

  // For some reason, possibly to do with React attempting to respect the Dom received from the server, setting the "maxLength" tag depending on whether or not we are in the browser does not work. This blanks it immediately after the initial render, which means that the server-side no-JS version never reaches it, and the client-side JS version never sees the maxlength attribute.
  useEffect(() => setCharacterLimitParam(undefined), []);

  return (
    <>
      {invalidMessage ? <ErrorMessage type={type} id={id} message={invalidMessage} /> : null}
      <Tag
        type={types[type]}
        autoComplete={autoCompletes[type]}
        pattern={patterns[type]}
        spellCheck={type === "TEXT"}
        className="form-control"
        id={id}
        name={name}
        placeholder={placeholder}
        defaultValue={defaultValue}
        required={required}
        onChange={onChange}
        onInvalid={onChange}
        aria-describedby={`${id}--max-chars ${id}--chars-left ${id}--chars-shorthand`}
        maxLength={characterLimitParam}
      />
      <CharacterLimit characterLimit={characterLimit} value={value} id={id} />
    </>
  );
}

function CharacterLimit({ characterLimit, value, id }: { characterLimit: number | null; value: string; id: string }) {
  const translate = useTranslate();

  if (!characterLimit) return null;

  const length = value.length;
  const left = characterLimit - length;
  const leftThreshold = Math.min(characterLimit - 100, characterLimit * 0.75);

  return (
    <div className={`character-limit ${length > characterLimit ? "character-limit--invalid" : ""}`}>
      <span className="character-limit__maximum" id={`${id}--max-chars`}>
        {translate("maxCharacters", { count: characterLimit })}
      </span>
      <span
        className={`character-limit__remaining ${length >= leftThreshold ? "" : "character-limit__remaining--hidden"}`}
        aria-live="polite"
        id={`${id}--chars-left`}
      >
        {left >= 0 ? translate("charactersLeft", { count: left }) : translate("charactersOver", { count: -left })}
      </span>
      <span className="character-limit__shorthand" aria-live="polite" id={`${id}--chars-shorthand`}>
        {translate("maxCharactersShorthand", { used: length, limit: characterLimit })}
      </span>
    </div>
  );
}
